import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Check, Zap, RefreshCw, Mail, Sparkles, Target, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';

interface PricingModalProps {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({ open, onClose, onUpgrade }) => {
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'onetime'>('onetime');

  const handlePurchase = (plan: 'monthly' | 'onetime') => {
    console.log('Redirecting to Stripe checkout for:', plan);
    
    // Show loading toast
    toast({
      title: "Redirecting to checkout...",
      description: "Please wait while we redirect you to secure payment.",
    });
    
    // Redirect to Stripe payment links
    setTimeout(() => {
      if (plan === 'onetime') {
        // One-time payment $9.99
        window.location.href = 'https://buy.stripe.com/cNifZj4wubwH63Xawd7IY03';
      } else {
        // Monthly subscription $15/month - UPDATED LINK
        window.location.href = 'https://buy.stripe.com/6oU00lfb8fMX8c57k17IY02';
      }
    }, 500);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-md"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative max-w-6xl w-full max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-purple-50/30 to-pink-50/30 rounded-3xl shadow-2xl"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg transition-all hover:scale-110"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="p-8 md:p-12">
            {/* Header */}
            <div className="text-center mb-10">
              <motion.div
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1.1, 1]
                }}
                transition={{ duration: 0.6, repeat: 2 }}
                className="inline-block mb-6"
              >
                <div className="relative">
                  <Crown className="h-20 w-20 text-purple-600" />
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full"
                  />
                </div>
              </motion.div>
              <h2 className="text-5xl font-bold mb-4 text-gradient">
                Choose Your Plan
              </h2>
              <p className="text-2xl text-gray-700 mb-3 font-semibold">
                🔥 Discover Which Women Find You MOST Attractive
              </p>
              <p className="text-lg text-purple-600 font-bold animate-pulse">
                ⚡ LIMITED TIME: Get instant access for just $9.99!
              </p>
            </div>

            {/* Plan Toggle */}
            <div className="flex justify-center mb-10">
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-full p-1.5 inline-flex shadow-lg">
                <button
                  onClick={() => setSelectedPlan('onetime')}
                  className={`px-8 py-3 rounded-full transition-all font-semibold ${
                    selectedPlan === 'onetime'
                      ? 'bg-white shadow-lg scale-105 text-purple-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  One-Time Payment
                  <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                    🔥 BEST DEAL
                  </span>
                </button>
                <button
                  onClick={() => setSelectedPlan('monthly')}
                  className={`px-8 py-3 rounded-full transition-all font-semibold ${
                    selectedPlan === 'monthly'
                      ? 'bg-white shadow-lg scale-105 text-purple-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Monthly Pro
                </button>
              </div>
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              {/* One-Time Payment Plan */}
              <motion.div
                animate={selectedPlan === 'onetime' ? { scale: 1.02 } : { scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Card className={`p-8 transition-all relative overflow-hidden ${
                  selectedPlan === 'onetime' 
                    ? 'ring-4 ring-purple-500 shadow-2xl bg-white' 
                    : 'opacity-70 bg-white/80'
                }`}>
                  <div className="absolute top-0 right-0 bg-gradient-to-br from-green-500 to-emerald-600 text-white text-sm font-bold px-6 py-2 rounded-bl-2xl shadow-lg">
                    ⚡ INSTANT ACCESS
                  </div>
                  
                  <div className="text-center mb-8 mt-4">
                    <Zap className="h-14 w-14 text-purple-600 mx-auto mb-4" />
                    <h3 className="text-3xl font-bold mb-3">Complete Analysis</h3>
                    <div className="mb-4">
                      <div className="flex items-center justify-center gap-3 mb-2">
                        <span className="text-3xl text-gray-400 line-through">$49.99</span>
                        <span className="text-6xl font-bold text-gradient">$9.99</span>
                      </div>
                      <p className="text-sm text-green-600 font-bold">ONE-TIME PAYMENT • Save 80%</p>
                    </div>
                    <div className="inline-block bg-yellow-100 border-2 border-yellow-400 rounded-full px-4 py-2 mb-2">
                      <p className="text-sm font-bold text-yellow-800">🎁 Pay once, keep forever!</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base"><strong>Full access to all 50+ cities</strong> worldwide ratings</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base"><strong>16 venue types analyzed</strong> (gym, club, park, etc.)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base"><strong>Complete demographic breakdown</strong> (Latina, Asian, Middle Eastern, White, Black women)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base"><strong>Ethnicity prediction</strong> & cultural analysis</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base">Detailed facial feature analysis</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base">Personalized attraction strategies</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Sparkles className="h-6 w-6 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base font-semibold text-purple-600">Lifetime access - no recurring fees!</span>
                    </li>
                  </ul>

                  <Button
                    className="w-full h-14 text-lg gradient-primary text-white hover:opacity-90 shadow-lg hover:shadow-xl transition-all"
                    size="lg"
                    onClick={() => handlePurchase('onetime')}
                  >
                    <Zap className="h-6 w-6 mr-2" />
                    Get Instant Access - $9.99
                  </Button>
                  <p className="text-center text-xs text-gray-500 mt-3">
                    ✅ Secure payment • No hidden fees • Instant delivery
                  </p>
                </Card>
              </motion.div>

              {/* Monthly Plan */}
              <motion.div
                animate={selectedPlan === 'monthly' ? { scale: 1.02 } : { scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Card className={`p-8 transition-all relative overflow-hidden ${
                  selectedPlan === 'monthly' 
                    ? 'ring-4 ring-blue-500 shadow-2xl bg-white' 
                    : 'opacity-70 bg-white/80'
                }`}>
                  <div className="absolute top-0 right-0 bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-bold px-6 py-2 rounded-bl-2xl shadow-lg">
                    🔄 MONTHLY UPDATES
                  </div>
                  
                  <div className="text-center mb-8 mt-4">
                    <RefreshCw className="h-14 w-14 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-3xl font-bold mb-3">Monthly Pro</h3>
                    <div className="mb-4">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <span className="text-6xl font-bold text-blue-600">$15</span>
                        <span className="text-2xl text-gray-500">/month</span>
                      </div>
                      <p className="text-sm text-blue-600 font-semibold">Cancel anytime • No commitment</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base font-semibold">Everything in One-Time Plan, PLUS:</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <RefreshCw className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base"><strong>NEW personalized advice every month</strong> based on your target demographic</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Target className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base"><strong>Custom strategies</strong> - Tell us which type of women you want to attract</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <TrendingUp className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base">Monthly progress tracking & optimization</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Sparkles className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base">Updated grooming & style trends</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Crown className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-base">Priority support & early feature access</span>
                    </li>
                  </ul>

                  <Button
                    className="w-full h-14 text-lg bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all"
                    size="lg"
                    onClick={() => handlePurchase('monthly')}
                  >
                    <RefreshCw className="h-6 w-6 mr-2" />
                    Start Monthly Pro - $15/mo
                  </Button>
                  <p className="text-center text-xs text-gray-500 mt-3">
                    🔄 Cancel anytime • Billed monthly • Instant access
                  </p>
                </Card>
              </motion.div>
            </div>

            {/* Comparison Section */}
            <div className="mb-8 p-6 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl border-2 border-purple-200">
              <h4 className="text-center font-bold text-xl mb-4 text-gray-800">💡 Which Plan is Right for You?</h4>
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div className="bg-white rounded-xl p-4 shadow-md">
                  <h5 className="font-bold text-purple-600 mb-2 flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Choose One-Time ($9.99) if:
                  </h5>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ You want instant complete analysis</li>
                    <li>✓ You prefer one payment and lifetime access</li>
                    <li>✓ You want to save money (80% off!)</li>
                    <li>✓ You're ready to take action now</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-md">
                  <h5 className="font-bold text-blue-600 mb-2 flex items-center gap-2">
                    <RefreshCw className="h-5 w-5" />
                    Choose Monthly ($15/mo) if:
                  </h5>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ You want ongoing personalized coaching</li>
                    <li>✓ You're targeting specific demographics</li>
                    <li>✓ You want monthly updated strategies</li>
                    <li>✓ You want to track progress over time</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="text-center space-y-4">
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>🔒 Secure Stripe Payment</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>✅ 30-Day Money-Back Guarantee</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>⚡ Instant Access</span>
                </div>
              </div>
              
              <p className="text-xs text-gray-500">
                ⭐ Trusted by users worldwide
              </p>
              
              <div className="pt-4 border-t border-gray-200">
                <a 
                  href="mailto:karims11@icloud.com"
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  <span>Questions? Contact us: karims11@icloud.com</span>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PricingModal;